import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  Users,
  Mail,
  Phone,
  Calendar,
  User,
  AlertCircle,
  CheckCircle,
  BarChart3,
  ArrowLeft,
  Send,
  Check,
  X,
  Mail as MailIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const InstructorManagement = () => {
  const { token } = useAuth();
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [notification, setNotification] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ─── Onboarding Stats ───
  const [showOnboardingStats, setShowOnboardingStats] = useState(false);
  const [onboardingStats, setOnboardingStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [emailConfirmation, setEmailConfirmation] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [statsPage, setStatsPage] = useState(1);
  const STATS_PER_PAGE = 20;
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);
  const [bulkEmailConfirmation, setBulkEmailConfirmation] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    status: "active",
  });

  const API_BASE_URL = "https://trainer-portal-78f5.onrender.com/api";

  // Fetch all instructors
  const fetchInstructors = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/trainers`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setInstructors(data.trainers || []);
      } else {
        const errorData = await response.json();
        showNotification(
          errorData.error || "Failed to fetch instructors",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching instructors:", error);
      showNotification("Error fetching instructors", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch onboarding stats
  const fetchOnboardingStats = async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/onboarding-stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOnboardingStats(data.stats || []);
      } else {
        const errorData = await response.json();
        showNotification(
          errorData.error || "Failed to fetch onboarding stats",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching onboarding stats:", error);
      showNotification("Error fetching onboarding stats", "error");
    } finally {
      setStatsLoading(false);
    }
  };

  // Send onboarding email
  const handleSendEmail = async (email, instructorName) => {
    setSendingEmail(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/send-onboarding-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, instructorName }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        showNotification(
          `Email sent successfully to ${instructorName || email}!`,
          "success"
        );
      } else {
        showNotification(data.error || "Failed to send email", "error");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      showNotification("Error sending email", "error");
    } finally {
      setSendingEmail(false);
      setEmailConfirmation(null);
    }
  };

  // Toggle onboarding stats view
  const toggleOnboardingStats = () => {
    if (!showOnboardingStats) {
      fetchOnboardingStats();
    }
    setShowOnboardingStats(!showOnboardingStats);
    setStatsPage(1);
  };

  // Send bulk onboarding email to all incomplete instructors
  const handleSendBulkEmail = async () => {
    setSendingBulkEmail(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/send-bulk-onboarding-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        showNotification(
          `${data.sent} email(s) sent successfully${data.failed > 0 ? `, ${data.failed} failed` : ""}!`,
          data.failed > 0 ? "error" : "success"
        );
      } else {
        showNotification(data.error || "Failed to send bulk emails", "error");
      }
    } catch (error) {
      console.error("Error sending bulk emails:", error);
      showNotification("Error sending bulk emails", "error");
    } finally {
      setSendingBulkEmail(false);
      setBulkEmailConfirmation(false);
    }
  };

  // Create new instructor
  const createInstructor = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/trainers`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showNotification("Instructor created successfully!", "success");
        closeModal();
        fetchInstructors();
      } else {
        showNotification(data.error || "Failed to create instructor", "error");
      }
    } catch (error) {
      console.error("Error creating instructor:", error);
      showNotification("Error creating instructor", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Update instructor
  const updateInstructor = async () => {
    setSubmitting(true);
    try {
      const updateData = {
        name: formData.name,
        phone: formData.phone,
        status: formData.status,
        password: formData.password, // optional
      };

      // Only include password if it's provided
      if (formData.password && formData.password.trim()) {
        updateData.password = formData.password;
      }

      const response = await fetch(
        `${API_BASE_URL}/admin/trainers/${encodeURIComponent(
          editingInstructor.email
        )}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        }
      );

      const data = await response.json();

      if (response.ok) {
        showNotification("Instructor updated successfully!", "success");
        closeModal();
        fetchInstructors();
      } else {
        showNotification(data.error || "Failed to update instructor", "error");
      }
    } catch (error) {
      console.error("Error updating instructor:", error);
      showNotification("Error updating instructor", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete instructor
  const deleteInstructor = async (email) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/trainers/${encodeURIComponent(email)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        showNotification("Instructor deleted successfully!", "success");
        fetchInstructors();
      } else {
        showNotification(data.error || "Failed to delete instructor", "error");
      }
    } catch (error) {
      console.error("Error deleting instructor:", error);
      showNotification("Error deleting instructor", "error");
    } finally {
      setDeleteConfirmation(null);
    }
  };

  // Show notification
  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (editingInstructor) {
      // Updating instructor - password not required
      if (!formData.name || !formData.email) {
        showNotification("Name and email are required", "error");
        return;
      }
      updateInstructor();
    } else {
      // Creating instructor - all fields required
      if (!formData.name || !formData.email || !formData.password) {
        showNotification("Please fill in all required fields", "error");
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        showNotification("Please enter a valid email address", "error");
        return;
      }

      createInstructor();
    }
  };

  // Open modal for creating new instructor
  const openCreateModal = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      phone: "",
      status: "active",
    });
    setEditingInstructor(null);
    setIsModalOpen(true);
  };

  // Open modal for editing instructor
  const openEditModal = (instructor) => {
    setFormData({
      name: instructor.name || "",
      email: instructor.email || "",
      password: "", // Don't populate password for editing
      phone: instructor.phone || "",
      status: instructor.status || "active",
    });
    setEditingInstructor(instructor);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingInstructor(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      phone: "",
      status: "active",
    });
    setShowPassword(false);
  };

  // Handle input change
  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Confirm delete
  const confirmDelete = (instructor) => {
    setDeleteConfirmation(instructor);
  };

  // Filter instructors based on search term
  const filteredInstructors = instructors.filter(
    (instructor) =>
      instructor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instructor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instructor.phone?.includes(searchTerm)
  );

  // Filter onboarding stats based on search term
  const filteredStats = onboardingStats.filter(
    (stat) =>
      stat.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stat.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination for stats
  const totalStatsPages = Math.ceil(filteredStats.length / STATS_PER_PAGE);
  const paginatedStats = filteredStats.slice(
    (statsPage - 1) * STATS_PER_PAGE,
    statsPage * STATS_PER_PAGE
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setStatsPage(1);
  }, [searchTerm]);

  useEffect(() => {
    fetchInstructors();
  }, []);

  return (
    <div className="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto">
      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 p-3 rounded-lg shadow-lg flex items-center gap-2 max-w-sm ${
            notification.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {notification.type === "success" ? (
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          <p className="text-xs">{notification.message}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {showOnboardingStats && (
            <button
              onClick={toggleOnboardingStats}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs">Back</span>
            </button>
          )}
          {!showOnboardingStats && (
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white rounded-lg hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <Plus className="w-4 h-4" />
              <span className="text-xs">Add Instructor</span>
            </button>
          )}
          <button
            onClick={toggleOnboardingStats}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 ${
              showOnboardingStats
                ? "bg-[#221E45] text-white shadow-lg"
                : "bg-white border border-[#221E45] text-[#221E45] hover:bg-[#221E45]/5"
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span className="text-xs">Onboarding Stats</span>
          </button>
          {showOnboardingStats && onboardingStats.filter(s => !s.onboardingCompleted).length > 0 && (
            <button
              onClick={() => setBulkEmailConfirmation(true)}
              disabled={sendingBulkEmail}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FC7A69] to-[#e45c53] text-white rounded-lg hover:shadow-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MailIcon className="w-4 h-4" />
              <span className="text-xs">
                {sendingBulkEmail ? "Sending..." : `Send All Incomplete (${onboardingStats.filter(s => !s.onboardingCompleted).length})`}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4 relative sticky top-0 ">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder={
            showOnboardingStats
              ? "Search by name or email..."
              : "Search instructors..."
          }
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full pl-10 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FC7A69] focus:border-[#FC7A69] transition-colors"
        />
      </div>

      {/* ─────────── ONBOARDING STATS TABLE VIEW ─────────── */}
      {showOnboardingStats ? (
        <>
          {statsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FC7A69]"></div>
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3">
                  <p className="text-xs text-gray-500">Total Instructors</p>
                  <p className="text-lg font-bold text-gray-900">
                    {onboardingStats.length}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-green-100 p-3">
                  <p className="text-xs text-gray-500">Completed</p>
                  <p className="text-lg font-bold text-green-600">
                    {
                      onboardingStats.filter((s) => s.onboardingCompleted)
                        .length
                    }
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-red-100 p-3">
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-lg font-bold text-red-600">
                    {
                      onboardingStats.filter((s) => !s.onboardingCompleted)
                        .length
                    }
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-blue-100 p-3">
                  <p className="text-xs text-gray-500">Avg Score</p>
                  <p className="text-lg font-bold text-blue-600">
                    {onboardingStats.filter((s) => s.onboardingScore !== null)
                      .length > 0
                      ? Math.round(
                          onboardingStats
                            .filter((s) => s.onboardingScore !== null)
                            .reduce((sum, s) => sum + s.onboardingScore, 0) /
                            onboardingStats.filter(
                              (s) => s.onboardingScore !== null
                            ).length
                        ) + "%"
                      : "—"}
                  </p>
                </div>
              </div>

              {/* Stats Table */}
              <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead>
                      <tr className="bg-gradient-to-r from-[#221E45] to-[#302b63]">
                        <th className="px-3 py-3 text-left text-xs font-semibold text-white">
                          #
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-white">
                          Instructor Name
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-white">
                          Email
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-white">
                          Video Watched
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-white">
                          Quiz Score
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-white">
                          Completed On
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-white">
                          Send Email
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paginatedStats.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="px-3 py-8 text-center text-xs text-gray-500"
                          >
                            No instructors found.
                          </td>
                        </tr>
                      ) : (
                        paginatedStats.map((stat, index) => (
                          <tr
                            key={stat.email}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-3 py-3 text-xs text-gray-500">
                              {(statsPage - 1) * STATS_PER_PAGE + index + 1}
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 bg-gradient-to-br from-[#FC7A69]/20 to-[#221E45]/20 rounded-full flex items-center justify-center flex-shrink-0">
                                  <User className="w-3.5 h-3.5 text-[#FC7A69]" />
                                </div>
                                <span className="text-xs font-medium text-gray-900">
                                  {stat.name}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-xs text-gray-600">
                              {stat.email}
                            </td>
                            <td className="px-3 py-3 text-center">
                              {stat.onboardingCompleted ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                                  <Check className="w-3 h-3" />
                                  Yes
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                                  <X className="w-3 h-3" />
                                  No
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-center">
                              {stat.onboardingScore !== null ? (
                                <span
                                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                                    stat.onboardingScore >= 75
                                      ? "bg-green-100 text-green-700"
                                      : "bg-orange-100 text-orange-700"
                                  }`}
                                >
                                  {stat.onboardingScore}%
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-center text-xs text-gray-600">
                              {stat.onboardingCompletedAt
                                ? new Date(
                                    stat.onboardingCompletedAt
                                  ).toLocaleDateString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : "—"}
                            </td>
                            <td className="px-3 py-3 text-center">
                              <button
                                onClick={() => setEmailConfirmation(stat)}
                                disabled={stat.onboardingCompleted}
                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                                  stat.onboardingCompleted
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white hover:shadow-md hover:scale-105"
                                }`}
                              >
                                <Send className="w-3 h-3" />
                                {stat.onboardingCompleted
                                  ? "Completed"
                                  : "Send Email"}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalStatsPages > 1 && (
                <div className="flex items-center justify-between mt-4 px-1">
                  <p className="text-xs text-gray-500">
                    Showing {(statsPage - 1) * STATS_PER_PAGE + 1}–
                    {Math.min(statsPage * STATS_PER_PAGE, filteredStats.length)}{" "}
                    of {filteredStats.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setStatsPage((p) => Math.max(1, p - 1))}
                      disabled={statsPage === 1}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalStatsPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => setStatsPage(page)}
                          className={`w-8 h-8 text-xs rounded-lg transition-all ${
                            statsPage === page
                              ? "bg-[#221E45] text-white shadow"
                              : "border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      onClick={() =>
                        setStatsPage((p) => Math.min(totalStatsPages, p + 1))
                      }
                      disabled={statsPage === totalStatsPages}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {/* ─────────── NORMAL INSTRUCTOR GRID VIEW ─────────── */}
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FC7A69]"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredInstructors.map((instructor) => (
                <div
                  key={instructor.email}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden border border-gray-100"
                >
                  <div className="p-3">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="w-8 h-8 bg-gradient-to-br from-[#FC7A69]/20 to-[#221E45]/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-[#FC7A69]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-xs text-gray-900 truncate">
                            {instructor.name}
                          </h3>
                          <span
                            className={`inline-block px-1.5 py-0.5 rounded-full text-xs font-medium ${
                              instructor.status === "active"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {instructor.status || "Active"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs truncate">
                          {instructor.email}
                        </span>
                      </div>

                      {instructor.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-xs">{instructor.phone}</span>
                        </div>
                      )}

                      {instructor.createdAt && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-xs">
                            {new Date(instructor.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1 pt-2 border-t border-gray-100">
                      <button
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        onClick={() => openEditModal(instructor)}
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                      <button
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                        onClick={() => confirmDelete(instructor)}
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredInstructors.length === 0 && (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">
                No instructors found
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                {searchTerm
                  ? "Try adjusting your search terms."
                  : "Get started by adding your first instructor."}
              </p>
              {!searchTerm && (
                <button
                  onClick={openCreateModal}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white rounded-lg hover:shadow-lg transition-all duration-200"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-xs">Add First Instructor</span>
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-2">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-sm font-bold text-gray-900">
                {editingInstructor ? "Edit Instructor" : "Add New Instructor"}
              </h2>
              <p className="text-xs text-gray-600 mt-1">
                {editingInstructor
                  ? "Update instructor details"
                  : "Create a new trainer account"}
              </p>
            </div>

            <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FC7A69] focus:border-[#FC7A69] transition-colors"
                  placeholder="Enter instructor's full name"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FC7A69] focus:border-[#FC7A69] transition-colors ${
                    editingInstructor ? "bg-gray-50 cursor-not-allowed" : ""
                  }`}
                  placeholder="instructor@example.com"
                  disabled={editingInstructor}
                  required
                />
                {editingInstructor && (
                  <p className="text-xs text-gray-500 mt-1">
                    Email cannot be changed
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Password {!editingInstructor && "*"}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 pr-10 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FC7A69] focus:border-[#FC7A69] transition-colors"
                    placeholder={
                      editingInstructor
                        ? "Enter new password (optional)"
                        : "Create a secure password"
                    }
                    required={!editingInstructor}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {editingInstructor && (
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to keep current password
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FC7A69] focus:border-[#FC7A69] transition-colors"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              {editingInstructor && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FC7A69] focus:border-[#FC7A69] transition-colors"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white rounded-lg hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                >
                  {submitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      <span className="text-xs">
                        {editingInstructor ? "Updating..." : "Creating..."}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs">
                      {editingInstructor ? "Update" : "Create"} Instructor
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-2">
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Delete Instructor
                  </h3>
                  <p className="text-xs text-gray-600">
                    This action cannot be undone
                  </p>
                </div>
              </div>

              <p className="text-xs text-gray-700 mb-4">
                Are you sure you want to delete{" "}
                <strong>{deleteConfirmation.name}</strong>? This will
                permanently remove their account and all associated data.
              </p>

              <div className="flex gap-2">
                <button
                  onClick={() => setDeleteConfirmation(null)}
                  className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteInstructor(deleteConfirmation.email)}
                  className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Confirmation Modal */}
      {emailConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-2">
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Send Onboarding Reminder
                  </h3>
                  <p className="text-xs text-gray-600">
                    Send email to instructor
                  </p>
                </div>
              </div>

              <p className="text-xs text-gray-700 mb-4">
                Send an onboarding reminder email to{" "}
                <strong>{emailConfirmation.name}</strong> at{" "}
                <strong>{emailConfirmation.email}</strong>?
              </p>

              <div className="flex gap-2">
                <button
                  onClick={() => setEmailConfirmation(null)}
                  disabled={sendingEmail}
                  className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleSendEmail(
                      emailConfirmation.email,
                      emailConfirmation.name
                    )
                  }
                  disabled={sendingEmail}
                  className="flex-1 px-3 py-2 bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white rounded-lg hover:shadow-lg transition-all duration-200 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingEmail ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      <span>Sending...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-1">
                      <Send className="w-3 h-3" />
                      <span>Send Email</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Email Confirmation Modal */}
      {bulkEmailConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-2">
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MailIcon className="w-4 h-4 text-orange-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Send Bulk Onboarding Emails
                  </h3>
                  <p className="text-xs text-gray-600">
                    Email all incomplete instructors
                  </p>
                </div>
              </div>

              <p className="text-xs text-gray-700 mb-4">
                This will send an onboarding reminder email to{" "}
                <strong>
                  {onboardingStats.filter((s) => !s.onboardingCompleted).length}
                </strong>{" "}
                instructor(s) who haven't completed onboarding. Continue?
              </p>

              <div className="flex gap-2">
                <button
                  onClick={() => setBulkEmailConfirmation(false)}
                  disabled={sendingBulkEmail}
                  className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendBulkEmail}
                  disabled={sendingBulkEmail}
                  className="flex-1 px-3 py-2 bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white rounded-lg hover:shadow-lg transition-all duration-200 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingBulkEmail ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      <span>Sending...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-1">
                      <Send className="w-3 h-3" />
                      <span>Send All</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstructorManagement;
